import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Phone,
  Mail,
  Globe,
  MapPin,
  Save,
  Share2,
  Download,
  Linkedin,
  Instagram,
  Facebook,
  RotateCcw,
} from 'lucide-react';
import { settingsService } from '@/services/settingsService';
import { bdmService } from '@/services/bdmService';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const DEFAULT_CARD = {
  name: 'SHINU E M',
  title: 'Sales Manager',
  phone: '9633860898',
  email: 'info@dpldew.com',
  website: 'https://dpldew.com',
  location: 'Kerala, India',
  linkedin: '',
  instagram: '',
  facebook: '',
};

export default function Shinu() {
  const [flipped, setFlipped] = useState(false);
  const [card, setCard] = useState(DEFAULT_CARD);

  const handleImport = useCallback(async () => {
    try {
      const [bdms, company] = await Promise.all([
        bdmService.getBdms(),
        Promise.resolve(settingsService.getCompanyProfile()),
      ]);
      const shinuBdm =
        bdms.find(
          (b) =>
            b.name?.toLowerCase().includes('shinu') || b.phone === '9633860898'
        ) || bdms[0];

      setCard((prev) => ({
        ...prev,
        name: shinuBdm?.name?.toUpperCase() ?? prev.name,
        phone: shinuBdm?.phone ?? company?.phone ?? prev.phone,
        email: shinuBdm?.email ?? company?.email ?? prev.email,
        website: prev.website || 'https://dpldew.com',
        location: company?.address || prev.location,
      }));
      toast.success('Details imported from company & team');
    } catch {
      const company = settingsService.getCompanyProfile();
      setCard((prev) => ({
        ...prev,
        location: company?.address || prev.location,
        website: prev.website || 'https://dpldew.com',
      }));
      toast.success('Company details applied');
    }
  }, []);

  const handleSave = useCallback(() => {
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${card.name}`,
      `TITLE:${card.title}`,
      `TEL;TYPE=CELL:${card.phone.replace(/\s/g, '')}`,
      `EMAIL:${card.email}`,
      `URL:${card.website}`,
      `ADR:;;${card.location};;;;`,
      'END:VCARD',
    ].join('\n');
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shinu-em.vcf';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Contact saved as vCard');
  }, [card]);

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    const text = `${card.name} - ${card.title} | ${card.phone} | ${card.email}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${card.name} - Business Card`,
          text,
          url,
        });
        toast.success('Shared');
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  }, [card]);

  function copyToClipboard(str: string) {
    navigator.clipboard.writeText(str);
    toast.success('Link copied to clipboard');
  }

  const linkClass =
    'inline-flex items-center gap-2 text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary rounded';

  const SideContent = ({
    isBack,
    compact,
  }: {
    isBack?: boolean;
    compact?: boolean;
  }) => (
    <div className="flex flex-col h-full p-6 md:p-8">
      <div className={cn('flex flex-col', compact ? 'gap-2' : 'gap-4')}>
        <h1 className="text-xl md:text-2xl font-medium tracking-tight text-foreground">
          {card.name}
        </h1>
        <p className="text-sm text-muted-foreground">{card.title}</p>
        <a href={`tel:${card.phone}`} className={linkClass} onClick={(e) => e.stopPropagation()}>
          <Phone className="h-4 w-4 shrink-0" />
          {card.phone}
        </a>
        <a href={`mailto:${card.email}`} className={linkClass} onClick={(e) => e.stopPropagation()}>
          <Mail className="h-4 w-4 shrink-0" />
          {card.email}
        </a>
        <a
          href={card.website}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          onClick={(e) => e.stopPropagation()}
        >
          <Globe className="h-4 w-4 shrink-0" />
          {card.website.replace(/^https?:\/\//, '')}
        </a>
        {card.location && (
          <p className="flex items-center gap-2 text-muted-foreground text-sm">
            <MapPin className="h-4 w-4 shrink-0" />
            {card.location}
          </p>
        )}
        <div className="flex flex-wrap gap-3 pt-2">
          {card.linkedin && (
            <a
              href={card.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
              aria-label="LinkedIn"
              onClick={(e) => e.stopPropagation()}
            >
              <Linkedin className="h-5 w-5" />
            </a>
          )}
          {card.instagram && (
            <a
              href={card.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
              aria-label="Instagram"
              onClick={(e) => e.stopPropagation()}
            >
              <Instagram className="h-5 w-5" />
            </a>
          )}
          {card.facebook && (
            <a
              href={card.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
              aria-label="Facebook"
              onClick={(e) => e.stopPropagation()}
            >
              <Facebook className="h-5 w-5" />
            </a>
          )}
        </div>
      </div>
      <div
        className="mt-auto pt-6 flex flex-wrap gap-2"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Button variant="outline" size="sm" onClick={handleSave}>
          <Save className="h-4 w-4 mr-1" />
          Save
        </Button>
        <Button variant="outline" size="sm" onClick={handleShare}>
          <Share2 className="h-4 w-4 mr-1" />
          Share
        </Button>
        <Button variant="outline" size="sm" onClick={handleImport}>
          <Download className="h-4 w-4 mr-1" />
          Import details
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-foreground">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/10 blur-[120px]" />
        <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-emerald-500/10 blur-[100px]" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-4">
        <p className="text-sm text-muted-foreground mb-4">
          Click card to flip · transform-origin: 0
        </p>

        <div
          className="w-full max-w-md"
          style={{ perspective: '1000px' }}
        >
          <button
            type="button"
            className={cn(
              'w-full relative h-[420px] cursor-pointer',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4 focus-visible:ring-offset-slate-950'
            )}
            style={{
              transformStyle: 'preserve-3d',
              transformOrigin: '0 50%',
              transform: `rotateY(${flipped ? 180 : 0}deg)`,
            }}
            onClick={() => setFlipped((f) => !f)}
            aria-label={flipped ? 'Flip to front' : 'Flip to back'}
          >
            {/* Front face */}
            <div
              className="absolute inset-0 rounded-2xl border border-border bg-card text-card-foreground shadow-xl"
              style={{
                backfaceVisibility: 'hidden',
                transformStyle: 'preserve-3d',
              }}
            >
              <SideContent />
            </div>
            {/* Back face */}
            <div
              className="absolute inset-0 rounded-2xl border border-border bg-card text-card-foreground shadow-xl"
              style={{
                backfaceVisibility: 'hidden',
                transformStyle: 'preserve-3d',
                transform: 'rotateY(180deg)',
              }}
            >
              <div className="h-full flex flex-col">
                <p className="text-muted-foreground text-sm px-6 pt-6">
                  DPL DEW · Interior Solutions
                </p>
                <SideContent isBack compact />
              </div>
            </div>
          </button>
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="mt-4 text-muted-foreground"
          onClick={() => setFlipped((f) => !f)}
        >
          <RotateCcw className="h-4 w-4 mr-1" />
          Flip card
        </Button>
      </div>
    </div>
  );
}
